import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  HANDOVER_SESSION_KEY,
  refreshLogoutTimer,
} from "src/generator/utils/refreshLogoutTimer";
import { usePostApi } from "src/hooks";
import { useDispatch } from "src/redux/hooks";
import { setPermsProperty, setUserType } from "src/redux/slices/permsSlice";
import { setVersionMessage } from "src/redux/slices/preferencesSlice";

import {
  IPostLoginActions,
  IRotationModalProps,
  TLoginWarning,
} from "./login.model";

interface Props {
  setRotationModalProps: (props: IRotationModalProps) => void;
}

const OUTDATED_MODAL_MESSAGE =
  "You are using an outdated version of the desktop app. Please download the latest release.";
const OUTDATED_BANNER_MESSAGE = "Outdated desktop app — update recommended";

/**
 * Custom hook to handle user login functionality.
 *
 * @param setRotationModalProps - A function to set the properties of the rotation modal.
 * @returns An object containing:
 * - `handleLogin`: A function to handle the login process.
 * - `isLoading`: A boolean indicating whether the login request is in progress.
 * - `loginWarning`: Soft or hard OOD warning details, if any.
 * - `setLoginWarning`: A function to update the login warning state.
 * - `dismissLoginWarning`: Close soft OOD popup and continue into the app.
 * - `runPostLoginActions`: A function to execute post-login actions based on the user's page and type.
 */
const useLoginUser = ({ setRotationModalProps }: Props) => {
  const { post, isLoading } = usePostApi();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loginWarning, setLoginWarning] = useState<TLoginWarning>(null);

  const APP_DOWNLOAD_BASE_URL =
    "https://www.ljelogbooks.com/api/v1/apps/DeskApp/";

  const getDownloadLink = (serverLink?: string) => {
    if (serverLink) return serverLink;
    const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
    return isMac
      ? `${APP_DOWNLOAD_BASE_URL}eLogbooks-mac.dmg`
      : `${APP_DOWNLOAD_BASE_URL}eLogbooks-win.exe`;
  };

  const persistAuth = (res: {
    _userid: string | boolean;
    _session: string | null;
    _yachtid: string | null;
    _code?: string | null;
  }) => {
    const { _userid, _session, _yachtid, _code } = res;
    localStorage.setItem(
      "authData",
      JSON.stringify({ _userid, _session, _yachtid, _code }),
    );
    localStorage.removeItem(HANDOVER_SESSION_KEY);
    refreshLogoutTimer();
  };

  const runPostLoginActions = ({ page, usertype }: IPostLoginActions) => {
    if (page === "audit2") {
      dispatch(
        setPermsProperty({
          key: "perms",
          value: [
            "uploadfiles",
            "downloadfiles",
            "deletefiles",
            "viewfiles",
            "viewhistory",
          ],
        }),
      );
      dispatch(setUserType(usertype));
      navigate(usertype === 9 ? "/summary/reviews" : "/summary/audits");
    } else if (
      page === "management" &&
      (usertype === 1 || usertype === 4 || usertype === 5)
    ) {
      dispatch(setUserType(usertype));
      navigate("/management");
    } else {
      navigate("/home");
    }
  };

  const dismissLoginWarning = () => {
    if (!loginWarning || loginWarning.denied) return;
    const { page, usertype } = loginWarning;
    setLoginWarning(null);
    if (page != null && usertype != null) {
      runPostLoginActions({ page, usertype });
    } else {
      navigate("/home");
    }
  };

  const handleLogin = async (params: any) => {
    await post({
      request: "login",
      body: { params },
      onSuccess: res => {
        if (res.data?.multi && res.data?.list) {
          setRotationModalProps({
            open: true,
            list: res.data.list,
            params,
          });
          return;
        }

        const downloadLink = getDownloadLink(res?.link);
        const isSoftOutdated =
          !!window?.api &&
          !res?.denied &&
          (res?.outdated || (res?.message && res?.link));

        if (isSoftOutdated) {
          persistAuth(res);
          dispatch(
            setVersionMessage({
              warning: true,
              message: OUTDATED_BANNER_MESSAGE,
              link: downloadLink,
            }),
          );
          setLoginWarning({
            message: res?.message || OUTDATED_MODAL_MESSAGE,
            link: downloadLink,
            denied: false,
            page: res.data?.page,
            usertype: res.data?.usertype,
          });
          return;
        }

        persistAuth(res);

        if (window?.api) {
          dispatch(
            setVersionMessage({
              message:
                "You are running the most up-to-date version of the desktop app",
            }),
          );
        }

        runPostLoginActions({
          page: res.data?.page,
          usertype: res.data?.usertype,
        });
      },
      onError: res => {
        if (res?.denied && res?.link) {
          setLoginWarning({
            message:
              typeof res.error === "string"
                ? res.error
                : "You're using an outdated version of the desktop app.",
            link: getDownloadLink(res.link),
            denied: true,
          });
        }
      },
    });
  };
  return {
    handleLogin,
    isLoading,
    loginWarning,
    setLoginWarning,
    dismissLoginWarning,
    runPostLoginActions,
  };
};

export default useLoginUser;
